import React, { useEffect, useState } from "react";
import { API_URL } from "../../utils/Url";
import { toast } from "react-toastify";
import { Header } from "../header/Header";

export default function AdminChatPanel() {
  const [rooms, setRooms] = useState([]);
  const [groupedUsers, setGroupedUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal & Deep Chat Selection States
  const [selectedUserGroup, setSelectedUserGroup] = useState(null);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const fetchAdminRooms = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem("token");

      const res = await fetch(`${API_URL}/api/chat/chatrooms-admin/by-admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data?.success) {
        setRooms(data.rooms);
        processGroupedUsers(data.rooms);
      }
    } catch (error) {
      console.error("Admin view interface sync error:", error);
      toast.error("Failed to load global server rooms payload.");
    } finally {
      setLoading(false);
    }
  };

  // 🛠️ Grouping Logics
  const processGroupedUsers = (allRooms) => {
    const userMap = {};

    allRooms.forEach((room) => {
      room.participants?.forEach((user) => {
        if (!user || !user._id) return;
        const peerNode = room.participants.find((p) => p && p._id !== user._id);

        if (!userMap[user._id]) {
          userMap[user._id] = {
            profile: user,
            connectionsCount: 0,
            connectedRooms: [],
          };
        }

        userMap[user._id].connectionsCount += 1;
        userMap[user._id].connectedRooms.push({
          roomId: room._id,
          totalMessages: room.totalMessages || 0,
          lastMessage: room.lastMessage || null,
          peer: peerNode || { name: "Unknown User", email: "N/A" },
        });
      });
    });

    setGroupedUsers(
      Object.values(userMap).sort(
        (a, b) => b.connectionsCount - a.connectionsCount,
      ),
    );
  };

  // 💬 Fetch Full Chat Conversation History for Admin Audit
  const fetchRoomMessages = async (roomId) => {
    try {
      setLoadingMessages(true);
      setActiveRoomId(roomId);
      const token = sessionStorage.getItem("token");

      const res = await fetch(
        `${API_URL}/api/chat/chatrooms-admin/messages/${roomId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await res.json();

      if (data?.success) {
        setChatMessages(data.messages || []);
      } else {
        // Fallback agar static mock arrays fetch setup dynamic array me data return kare
        setChatMessages(data || []);
      }
    } catch (error) {
      console.error("Error fetching explicit room chats:", error);
      toast.error("Failed to extract raw message node logs.");
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    fetchAdminRooms();
  }, []);

  return (
    <>
      <Header />
      <div className="mt-5 container-fluid py-4">
        {/* Upper Master Dashboard Banner */}
        <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
          <div>
            <h2 className="fw-bold text-dark mb-1">User Connection Logs</h2>
            <p className="text-muted small mb-0">
              Unique Active Interacting Users:{" "}
              <span className="badge bg-primary">{groupedUsers.length}</span>
            </p>
          </div>
          <button
            onClick={fetchAdminRooms}
            className="btn btn-sm btn-outline-secondary"
          >
            🔄 Reload page
          </button>
        </div>

        {/* 📦 Master Grid */}
        <div className="row g-3">
          {groupedUsers.map((userNode) => (
            <div
              key={userNode.profile._id}
              className="col-12 col-md-6 col-lg-4 col-xl-3"
            >
              <div className="card shadow-sm h-100 border-light">
                <div className="card-body p-3 d-flex flex-column justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <img
                      src={
                        userNode?.profile?.userImages?.[0]?.picUrl
                          ? `${API_URL}${userNode.profile.userImages[0].picUrl}`
                          : "https://placehold.co/100x100?text=No+Photo"
                      }
                      alt={userNode.profile?.name}
                      className="rounded-circle border object-fit-cover"
                      style={{ width: "50px", height: "50px" }}
                    />
                    <div className="min-w-0 flex-grow-1">
                      <h6 className="fw-bold text-dark text-truncate mb-0 small">
                        {userNode.profile?.name}
                      </h6>
                      <span className="text-muted d-block text-truncate text-[11px]">
                        {userNode.profile?.email}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 pt-2 border-top d-flex justify-content-between align-items-center">
                    <span
                      className="text-muted small"
                      style={{ fontSize: "11px" }}
                    >
                      Active Connections:
                    </span>
                    <span className="badge bg-dark rounded-pill">
                      {userNode.connectionsCount} Channels
                    </span>
                  </div>

                  <button
                    onClick={() => {
                      setSelectedUserGroup(userNode);
                      setActiveRoomId(null); // Reset deep chat view on user change
                      setChatMessages([]);
                    }}
                    className="btn btn-sm btn-outline-primary w-100 mt-3 fw-bold text-uppercase"
                    style={{ fontSize: "11px" }}
                  >
                    Inspect Connections →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 🔐 TWO-PANEL INTERACTIVE AUDIT MODAL FRAMEWORK */}
      {selectedUserGroup && (
        <div
          className="modal show d-block fade-in bg-slate-950/60 backdrop-blur-xs"
          tabIndex="-1"
          style={{ zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="modal-header border-bottom bg-light px-4 py-3">
                <h5 className="modal-title fw-black text-slate-950">
                  Communications Control for:{" "}
                  <span className="text-primary">
                    {selectedUserGroup.profile.name}
                  </span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedUserGroup(null)}
                ></button>
              </div>

              <div className="modal-body p-0">
                <div className="row g-0" style={{ height: "65vh" }}>
                  {/* LEFT PANEL: Connected Channels/Peers List */}
                  <div className="col-12 col-md-5 border-end overflow-auto p-3 bg-light/30">
                    <p
                      className="text-secondary fw-bold text-uppercase tracking-wider mb-2"
                      style={{ fontSize: "10px" }}
                    >
                      Click A Connection To Read Logs (
                      {selectedUserGroup.connectionsCount})
                    </p>

                    <div className="d-flex flex-column gap-2">
                      {selectedUserGroup.connectedRooms.map((roomNode, idx) => (
                        <div
                          key={idx}
                          onClick={() => fetchRoomMessages(roomNode.roomId)}
                          className={`p-3 rounded-3 border transition-all cursor-pointer ${
                            activeRoomId === roomNode.roomId
                              ? "bg-primary text-white border-primary shadow-sm"
                              : "bg-white text-dark hover:bg-light border-gray-100"
                          }`}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <img
                              src={
                                roomNode?.profile?.userImages?.[0]?.picUrl
                                  ? `${API_URL}${roomNode.profile.userImages[0].picUrl}`
                                  : "https://placehold.co/100x100?text=No+Photo"
                              }
                              alt={roomNode.peer?.name}
                              className="rounded-circle border"
                              style={{
                                width: "36px",
                                height: "36px",
                                objectFit: "cover",
                              }}
                            />
                            <div className="flex-grow-1 min-w-0">
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-bold small text-truncate">
                                  {roomNode.peer?.name}
                                </span>
                                <span
                                  className={`badge ${activeRoomId === roomNode.roomId ? "bg-white text-primary" : "bg-info text-dark"}`}
                                  style={{ fontSize: "9px" }}
                                >
                                  {roomNode.totalMessages} Msg
                                </span>
                              </div>
                              <small
                                className={`d-block text-truncate mt-1 ${activeRoomId === roomNode.roomId ? "text-white/80" : "text-muted"}`}
                                style={{ fontSize: "11px" }}
                              >
                                Last:{" "}
                                {roomNode.lastMessage?.text ||
                                  "No logs available."}
                              </small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* RIGHT PANEL: Live Chat Conversation Stream Reader */}
                  <div className="col-12 col-md-7 d-flex flex-column bg-white h-100">
                    {activeRoomId ? (
                      loadingMessages ? (
                        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1 text-muted">
                          <div className="spinner-border spinner-border-sm text-primary mb-2"></div>
                          <span className="small">
                            Decrypting conversations secure packets...
                          </span>
                        </div>
                      ) : (
                        <>
                          {/* Chat Header inside panel */}
                          <div className="p-3 border-bottom bg-light/50 d-flex justify-content-between align-items-center">
                            <span className="fw-bold text-dark small">
                              📜 Conversation Stream Audit
                            </span>
                            <span
                              className="text-muted font-monospace"
                              style={{ fontSize: "10px" }}
                            >
                              Room ID: {activeRoomId}
                            </span>
                          </div>

                          {/* Messages Thread Container */}
                          <div
                            className="flex-grow-1 overflow-auto p-3 bg-slate-50 d-flex flex-column gap-2"
                            style={{ maxHeight: "50vh" }}
                          >
                            {chatMessages.length === 0 ? (
                              <div className="text-center text-muted my-auto small">
                                Empty pipeline node. No texts inside this room
                                framework.
                              </div>
                            ) : (
                              chatMessages.map((msg, mIdx) => {
                                // Match mapping identifier configurations
                                const isFromMainUser =
                                  msg.sender ===
                                    selectedUserGroup.profile._id ||
                                  msg.sender?._id ===
                                    selectedUserGroup.profile._id;

                                return (
                                  <div
                                    key={mIdx}
                                    className={`d-flex flex-column max-w-75 rounded-3 p-1 shadow-xs ${
                                      isFromMainUser
                                        ? "bg-light text-gray align-self-end text-end"
                                        : "bg-light text-dark align-self-start text-start border"
                                    }`}
                                    style={{ maxWidth: "80%" }}
                                  >
                                    <span
                                      className="d-block small fw-semibold"
                                      style={{ fontSize: "11px" }}
                                    >
                                      {isFromMainUser
                                        ? selectedUserGroup.profile.name
                                        : "Recipient Peer"}
                                    </span>
                                    <p className="mb-0 mt-0.5 text-break small font-monospace">
                                      {msg.messageText || msg.text}
                                    </p>
                                    <span
                                      className="text-muted d-block mt-1 opacity-75"
                                      style={{ fontSize: "8px" }}
                                    >
                                      {new Date(
                                        msg.createdAt,
                                      ).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </>
                      )
                    ) : (
                      <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1 text-muted py-5 px-3 text-center">
                        <span className="text-3xl mb-2">💬</span>
                        <h6 className="fw-bold text-secondary">
                          No Conversation Selected
                        </h6>
                        <p
                          className="small text-gray-400 mb-0"
                          style={{ maxWidth: "250px" }}
                        >
                          Select any individual link from the left dashboard
                          list channels stream.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer bg-light border-top p-2">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary px-4 text-xs font-bold text-uppercase rounded-2"
                  onClick={() => setSelectedUserGroup(null)}
                >
                  Close Logs
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
